import { useMemo, useState } from "react";
import {
  Box,
  OptionPanelSection,
  OptionPanelSeparator,
  Text,
  useDrawerPages,
  useIsMobile,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import type { IssueScope } from "#app/lib/filters/issue-scope";
import { scopeWorkflowId } from "#app/lib/filters/issue-scope";
import { hasQuickFilterValue } from "#app/lib/filters/quick-values";
import { scopeQuickFilterDefs } from "#app/lib/filters/scoped-quick-filters";
import type {
  FilterConfig,
  QuickFilterLayerFor,
  UseFiltersResult,
} from "#app/lib/filters/types";
import { isDrillableSection, QuickFilterPage } from "./quick-filter-page";
import { QuickFilterPopupRow, QuickFilterRow } from "./quick-filter-row";
import { QuickFilterSearch } from "./quick-filter-search";
import {
  useQuickFilterOptions,
  type QuickFilterSection,
} from "./quick-inputs/quick-filter-options";
import { useAssigneeRoster } from "./use-assignee-roster";
import { useFilterTypeAvailability } from "./use-filter-type-availability";

type QuickFiltersProps = Omittable<{
  config: FilterConfig;
  filters: UseFiltersResult;
  /** The screen's scope, when it has one. A value the scope already pins to
   *  exactly one is not worth asking about — see `scopeQuickFilterDefs`. */
  scope?: IssueScope;
  /** The layer each field lives in — see `QuickFilterLayerFor`. Defaults to
   *  `filters` for every field. */
  layerFor?: QuickFilterLayerFor;
}>;

type RowGroup = {
  key: string;
  /** Absent for an ungrouped run — the archived flag stands alone. */
  title: string | undefined;
  sections: QuickFilterSection[];
};

/** Rows collected under their `group`, in first-appearance order. */
function groupSections(sections: QuickFilterSection[]): RowGroup[] {
  const groups: RowGroup[] = [];
  const byKey = new Map<string, RowGroup>();
  for (const section of sections) {
    const key = section.def.group ?? "";
    let group = byKey.get(key);
    if (!group) {
      group = { key, title: section.def.group, sections: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.sections.push(section);
  }
  return groups;
}

/**
 * The Simple tab as a LIST: ONE search over every filter's values, and the
 * filters themselves as a grouped settings list.
 *
 * That list grammar is `OptionPanel`, which is what the Customize view panel's
 * other pages are built from. This is the panel's Filters page (`ViewFilters`,
 * editing a saved view's filters), reached by drilling one row deeper — so it
 * renders as the same kind of list it was opened from rather than switching
 * languages on arrival — and it is the mobile filter drawer's Simple tab.
 *
 * A row OPENS ITS VALUES one filter at a time, and the viewport decides
 * where. On desktop a row opens its values in a popup BESIDE the panel on
 * HOVER (`QuickFilterPopupRow`), the way the toolbar menu's rows open their
 * submenus: sliding from Assignee to Stage swaps the popup with no press at
 * all, the other filters stay on screen, and there is nothing to go Back
 * from. On mobile a row DRILLS IN: a press pushes the filter's values as a
 * page over the list (`QuickFilterPage`, in the drawer's `DrawerPages`
 * stack), the way a phone's settings app descends a list. Touch has no hover,
 * a popup has nowhere to open beside a drawer that reaches both screen edges,
 * and a roster disclosed under its row would push the other filters off the
 * surface the reader opened to read. The page's header is the field's name
 * with a back chevron and a Clear for that one value; the list stays mounted
 * underneath, so the value reads back on its row the moment the page pops.
 *
 * The panel therefore carries NO applied-values band on its root. Every
 * applied value is already on its own row (`QuickFilterValue`), and a chip
 * repeating it is the same fact in two places, disagreeing the moment one of
 * them is wrong.
 *
 * Values flow through `filters.setQuickValue`, which updates blocks[0] of the
 * shared FilterState, and every surface reads and writes through the same value
 * index (`quick-filter-options.ts`) — so a search hit, a row's readout, a popup
 * and a disclosed list can never disagree about what a filter offers or what
 * is applied.
 */
export function QuickFilters({
  config,
  filters,
  scope,
  layerFor: layerForProp,
}: QuickFiltersProps) {
  const [query, setQuery] = useState("");
  const layerFor: QuickFilterLayerFor = layerForProp ?? (() => filters);
  // The one filter whose popup is open beside the panel on desktop, by field
  // key; null is the plain list. One at a time: hovering the next row swaps
  // the popup rather than stacking one. The mobile page stack keeps its own
  // open page (`useDrawerPages`).
  const [openField, setOpenField] = useState<string | null>(null);
  const isMobile = useIsMobile();
  // The drawer's page stack, on the surfaces that mount one. Rows drill into
  // it on a phone; without a stack (the desktop panel) rows open popups.
  const pages = useDrawerPages();
  const drillIn = isMobile && pages !== null;
  const isAvailable = useFilterTypeAvailability();

  const scopedWorkflowId = scope ? scopeWorkflowId(scope) : undefined;

  // Built off the config and the scope alone, so the index survives value
  // changes: the pools do not depend on what is selected in them.
  const defs = useMemo(
    () => scopeQuickFilterDefs(config.quickFilters ?? [], scope),
    [config.quickFilters, scope],
  );
  const roster = useAssigneeRoster(scope);
  const allSections = useQuickFilterOptions(
    defs,
    scopedWorkflowId,
    roster,
    (field) => layerFor(field).quickValues[field],
  );

  // A row over an empty pool is a dead end and hides — unless it holds an
  // applied value, which must stay inspectable and clearable.
  const sections = allSections.filter(
    ({ def }) =>
      isAvailable(def.type) ||
      hasQuickFilterValue(layerFor(def.field).quickValues[def.field]),
  );

  if (sections.length === 0) {
    return (
      <Box padding="lg">
        <Text size="sm" variant="muted">
          No quick filters configured for this view.
        </Text>
      </Box>
    );
  }

  const searching = query.trim().length > 0;
  // A popup's open and close both report here. Closing only clears the field
  // that owns the report: when hovering row B closes row A, B's open and A's
  // close land in either order, and A's close must not undo B's open.
  const setFieldOpen = (field: string, open: boolean) =>
    setOpenField((prev) => (open ? field : prev === field ? null : prev));

  return (
    <Box direction="column" paddingBottom="sm">
      <QuickFilterSearch
        sections={sections}
        layerFor={layerFor}
        onQueryChange={setQuery}
      />
      {searching
        ? null
        : groupSections(sections).map((group, index) => (
            <Box key={group.key}>
              {index > 0 ? <OptionPanelSeparator /> : null}
              <OptionPanelSection title={group.title}>
                {group.sections.map((section) =>
                  drillIn ? (
                    <QuickFilterRow
                      key={section.def.field}
                      section={section}
                      filters={layerFor(section.def.field)}
                      onOpen={pages.open}
                    />
                  ) : (
                    <QuickFilterPopupRow
                      key={section.def.field}
                      section={section}
                      filters={layerFor(section.def.field)}
                      open={openField === section.def.field}
                      onOpenChange={(open) =>
                        setFieldOpen(section.def.field, open)
                      }
                    />
                  ),
                )}
              </OptionPanelSection>
            </Box>
          ))}
      {/* The pages the rows push, one per filter that has values to open.
          They render here, beside the rows that own their data, and portal
          into the stack's layer over the list. A hidden row's page (its
          pool emptied while a search narrows the list) is still reachable
          only from its row, so the set follows the visible sections. */}
      {drillIn
        ? sections
            .filter(isDrillableSection)
            .map((section) => (
              <QuickFilterPage
                key={section.def.field}
                section={section}
                filters={layerFor(section.def.field)}
              />
            ))
        : null}
    </Box>
  );
}

